import PageCompanySelector from "@hd/auth/components/PageCompanySelector";
import { useAuth } from "@hd/auth/useAuth";
import { useTranslate } from "@hd/i18n/useTranslate";
import { SectionIds } from "@hd/types/shared/section-ids";
import { useCallback, useMemo } from "react";
import { useContactPanel } from "@hd/contact-panel/useContactPanel";
import { useWhatsappModal } from "@hd/whatsapp-modal/useWhatsappModal";
import { useOnlineContactIdsQuery } from "@hd/api/contact/queries/useOnlineContactIdsQuery";
import { ContactPanelTabIds } from "@hd/contact-panel/types/contact-panel-tab-ids";
import { CommunicationTypeEnum } from "@hd/types/dto/communication/communication-type.enum";
import { useScheduledCommunicationsQuery, scheduledCommunicationsQueryKey } from "@hd/api/communication/queries/useScheduledCommunicationsQuery";
import { InfiniteData, useQueryClient } from "@tanstack/react-query";
import { PagedResultDto } from "@hd/types/dto/shared/paged-result.dto";
import { ScheduledCommunication } from "@hd/types/dto/communication/scheduled-communication.dto";
import { useConfirmScheduledCommunicationMutation } from "@hd/api/communication/mutations/useConfirmScheduledCommunicationMutation";
import { useDeleteScheduledCommunicationMutation } from "@hd/api/communication/mutations/useDeleteScheduledCommunicationMutation";
import ScheduledCommunicationsTable from "./components/ScheduledCommunicationsTable";

function CommunicationPostStayPage() {
  const t = useTranslate();
  const { pageCompanyId: companyId } = useAuth();
  const queryClient = useQueryClient();

  const { open: openContactPanel } = useContactPanel();
  const { open: openWhatsappModal } = useWhatsappModal();  
  const { data: onlineContactIds } = useOnlineContactIdsQuery({
      companyId,
    });

  const confirmMutation = useConfirmScheduledCommunicationMutation();
  const cancelMutation = useDeleteScheduledCommunicationMutation();

  const postStayEmailsQueryParams = useMemo(() => {
      return {
        companyId,
        communicationType: [CommunicationTypeEnum.LeisurePostStay]
      };
    }, [companyId]);
  
    const postStayEmailsQuery = useScheduledCommunicationsQuery(
      postStayEmailsQueryParams
    );
  
    const postStayData = useMemo(
      () =>
        postStayEmailsQuery.data?.pages
          ?.flatMap((page) => page.items)
          .sort(
            (a, b) =>
              new Date(b.scheduledDateTime).getTime() - new Date(a.scheduledDateTime).getTime()
          ) ?? [],
      [postStayEmailsQuery.data]
    );
  
    const totalCount = useMemo(
      () => postStayEmailsQuery.data?.pages[0].totalCount,
      [postStayEmailsQuery.data]
    );

    const showContactPanel = useCallback(
        (contactId: number, companyId: number, tabId: ContactPanelTabIds) => {
          openContactPanel(contactId, companyId, tabId);
        },
        [openContactPanel]
      );

    const handleConfirmScheduledCommunication = useCallback(
      (selectedCommunicationIds: string[]) => {
        confirmMutation.mutate(
          selectedCommunicationIds,
          {
            onSuccess: () => {
              const queryKey = scheduledCommunicationsQueryKey({
                companyId,
                communicationType: [CommunicationTypeEnum.LeisurePostStay],
              });

              queryClient.setQueryData(queryKey, (oldData: InfiniteData<PagedResultDto<ScheduledCommunication>>) => {
                if (!oldData) return oldData;

                return {
                  ...oldData,
                  pages: oldData.pages.map((page: PagedResultDto<ScheduledCommunication>) => ({
                    ...page,
                    items: page.items.map((item: ScheduledCommunication) =>
                      selectedCommunicationIds.includes(item.id)
                        ? { ...item, confirmed: true }
                        : item
                    ),
                  })),
                };
              });
            },
          }
        );
      },
      [confirmMutation, companyId, queryClient]
    );

    const handleCancelScheduledCommunication = useCallback(
      (selectedCommunicationIds: string[]) => {
        cancelMutation.mutate(
          selectedCommunicationIds,
          {
            onSuccess: () => {
              const queryKey = scheduledCommunicationsQueryKey({
                companyId,
                communicationType: [CommunicationTypeEnum.LeisurePostStay],
              });

              queryClient.setQueryData(queryKey, (oldData: InfiniteData<PagedResultDto<ScheduledCommunication>>) => {
                if (!oldData) return oldData;

                // Il totalCount è lo stesso in tutte le pagine, quindi lo calcoliamo una volta sola
                const newTotalCount = (oldData.pages[0]?.totalCount ?? 0) - 1;

                return {
                  ...oldData,
                  pages: oldData.pages.map((page: PagedResultDto<ScheduledCommunication>) => ({
                    ...page,
                    items: page.items.filter(
                      (item: ScheduledCommunication) =>
                        !selectedCommunicationIds.includes(item.id)
                    ),
                    totalCount: newTotalCount,
                  })),
                };
              });
            },
          }
        );
      },
      [cancelMutation, companyId, queryClient]
    );
      
  return (
    <div className="p-5 flex-grow h-0 flex flex-col overflow-auto">
      <div className="flex mb-3 text-bodyTextLight">
        <div>
          <div className="text-xxl">{t("communicationPostStayPage.title")}</div>
          <div className="text-xl">{t("communicationPostStayPage.subtitle")}</div>
        </div>
      </div>
      <div className="border-t border-b border-gray-300 py-3 flex items-center mb-4">
        <PageCompanySelector
          sectionId={SectionIds.Communication}
        ></PageCompanySelector>
      </div>
      {companyId && (
        <div className="bg-white p-5">
          <ScheduledCommunicationsTable
            data={postStayData || []}
            isLoading={postStayEmailsQuery.isPending}
            isFetchingNextPage={postStayEmailsQuery.isFetchingNextPage}
            totalCount={totalCount || null}
            onlineContactIds={onlineContactIds || []}
            canLoadMore={postStayEmailsQuery.hasNextPage}
            isConfirming={confirmMutation.isPending}
            isCanceling={cancelMutation.isPending}
            onLoadMore={postStayEmailsQuery.fetchNextPage}
            onShowContactPanel={(contactId, tabIndex) =>
              showContactPanel(contactId, companyId, tabIndex)
            }
            onShowPhoneNumberModal={(contactId, quotationId) => {
              openWhatsappModal(null, companyId, contactId, CommunicationTypeEnum.LeisurePostStay, quotationId, false);
            }}
            onConfirm={handleConfirmScheduledCommunication}
            onCancel={handleCancelScheduledCommunication}></ScheduledCommunicationsTable>
        </div>
      )}
    </div>
  );
}

export default CommunicationPostStayPage;
